import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { frozenRateFields, rateOverrideFields, rateOverrideInputFields, rateOverridePatchFields, documentCurrencyInputFields, documentCurrencyPatchFields } from './fx.js';
import { DocumentLookupQuerySchema, DocumentLookupResponseSchema, DocumentNeighboursSchema, } from './document-navigation.js';
import { StatsCurrencyField } from './document-stats.js';
import { DocumentDateRangeQuery, pagedQuery, pagedResponse } from './pagination.js';
import { IsoDateSchema, MessageSchema, MoneyStringSchema, PctStringSchema, QtyStringSchema, UnitPrecisionSchema, } from './primitives.js';
import { TwoWayAmountSchema } from './sales-invoices.js';
const c = initContract();
/**
 * Purchase Returns / Debit Notes (PART 7, PUR-04 on the SAL-00 shared
 * layout) — the document that closes the product's one broken chain. Goods
 * could be received from a supplier and never sent back: `stock_movements`
 * has carried a `purchase_return` reference kind and a `supplier_return`
 * movement kind since the stock ledger was built, and nothing wrote either.
 *
 * It is the credit note (SAL-05) in a mirror, and it is built to that shape
 * on purpose:
 *   - it derives from a POSTED SUPPLIER BILL (BR-PUR-04-03), not a posted
 *     sales invoice, and the picker is limited to that supplier's posted
 *     bills;
 *   - the goods go OUT (kind `supplier_return`) instead of coming in;
 *   - a DEBIT note lowers what WE owe (DR 2111 Trade Payables) where a credit
 *     note lowers what the customer owes.
 *
 * Permission area: `purchasing`, per action:
 *   list/get = purchasing.view · create = purchasing.create ·
 *   edit draft = purchasing.edit · delete draft = purchasing.delete ·
 *   POST RETURN = purchasing.approve.
 * BR-PUR-04-11 names the prototype's single `exp.approve` key; PART 16.2.2
 * row 2 puts "purchase orders, goods receipts, supplier bills, purchase
 * returns, import orders" in the `purchasing` area, so the prototype's one
 * coarse key resolves onto the twelve-area grid as `purchasing.approve` —
 * the same key that posts a goods receipt and a bill, which is 16.3's F/X
 * read on the payable side: whoever may put a liability into the books
 * should be the one who may take it back out.
 *
 * The shape of a return: every line derives from ONE billed line; the unit
 * price, the line discount, the document discount rate, the VAT rate and the
 * exchange rate are COPIED from the bill and are not on the wire at all
 * (BR-PUR-04-06) — the caller chooses only WHICH lines and HOW MANY go back,
 * capped by billed − already returned over POSTED returns (BR-PUR-04-04) and
 * by what is actually on the shelf unless the return is value-only
 * (BR-PUR-04-05). Posting writes, in ONE transaction: the stock movements
 * out at the cost the goods ENTERED at (BR-PUR-04-07), then the §12 entry
 * whose goods leg the `restocked` flag chooses (BR-PUR-04-08). R9: only a
 * POSTED debit note reaches a supplier statement or a payables report.
 */
// ── The fixed sentences of PUR-04 §14 ───────────────────────────────────────
export const PURCHASE_RETURN_MESSAGES = {
    /** BR-PUR-04-03 / ERR-PUR-04-01, word for word. */
    pickBill: 'Pick the bill this return is against.',
    /** Wave 5D — a DIRECT return (no bill) names the supplier instead. */
    pickSupplier: 'Pick the supplier this return goes back to.',
    /** BR-PUR-04-04 / ERR-PUR-04-02, word for word, with the live numbers. */
    qtyCap: (billed, code, returned, left) => `The bill charged for ${billed} of ${code} and ${returned} have already gone back. You can return at most ${left} more.`,
    /** BR-PUR-04-05 / ERR-PUR-04-03, word for word, with the live numbers. */
    notInStock: (onHand, code, warehouse, wanted) => `Only ${onHand} of ${code} are in ${warehouse}. You cannot send back ${wanted}.`,
    /** BR-PUR-04-10 / ERR-PUR-04-04, word for word. */
    cancelledBill: (code) => `Bill ${code} was cancelled. There is nothing to debit.`,
    /**
     * BR-PUR-04-11 / ERR-PUR-04-05, word for word — the amber banner the screen
     * shows a user who does not hold `purchasing.approve`. The 403 itself keeps
     * PART 16.7's fixed sentence (a refusal never says WHICH permission is
     * missing); this is the screen's own copy, which may, because the user is
     * looking at their own return.
     */
    approvalBanner: "You don't have permission to approve this return — sent to an approver.",
    /** BR-PUR-04-06 / ERR-PUR-04-05's editor half. */
    pricesCopied: 'Prices on a return are copied from the original bill and cannot be changed.',
    /** PUR-04 §15's full-return rule, read from the other end. */
    debitCap: 'A return cannot debit more than the bill total, less debit notes already raised.',
    /** A return needs at least one line going back. */
    noLines: 'Pick at least one line to return.',
    /** R8 family (BR-PUR-04-13). */
    notADraft: 'Only a draft return can be posted.',
    postedLocked: 'This debit note is posted. Only the notes may change.',
    postedNoDelete: 'A posted debit note cannot be deleted.',
    /** §15's value-only return: nothing physical is left to send back. */
    nothingToMove: 'None of these goods are still in stock. Mark the return as value-only to claim the money without a stock movement.',
    /** BR-PUR-04-01: cancel is the posted state's correction; a draft deletes. */
    cancelOnlyPosted: 'Only a posted debit note can be cancelled. A draft is edited or deleted instead.',
    /** GEN-74: applied money rides the note — un-apply it first. */
    cancelAfterApplied: (status) => `This debit note is ${status}. Un-apply the money first — reverse the voucher that applied it.`,
    /** GEN-72: the cancelling act carries its reason. */
    cancelReasonRequired: 'A posted debit note can only be cancelled with a reason, which is written to the audit log.',
    /** BR-PUR-04-02: only a posted, unapplied note can be refunded in cash. */
    refundOnlyPosted: 'Only a posted debit note can be refunded. A credited note was already set against bills.',
    /** The credit is partly consumed — one door at a time (GEN-74's principle). */
    refundAfterApplied: 'Part of this credit is already applied to bills. Reverse the voucher that applied it before recording a cash refund.',
    /** PUR-04 §12 row 4: the money lands in a cash or bank account. */
    refundAccountKind: 'A supplier refund is deposited into a cash or bank account.',
};
/**
 * D46 — PUR-04's four parameterised debit-note refusals, structured. Every
 * param on `qtyCap` / `notInStock` is a PRE-FORMATTED quantity string from the
 * service's formatter (interpolated verbatim, nothing pluralises); `code` and
 * `warehouse` are display values. `cancelAfterApplied`'s `status` is a raw
 * TOKEN ('credited' / 'refunded') — en selects it back to the historical word,
 * ar says it in Arabic, so the token is absent from the Arabic render. Sentence
 * parity pinned by `refusal-census.spec.ts`.
 */
export const PURCHASE_RETURN_REFUSALS = {
    qtyCap: (billed, code, returned, left) => ({
        message: PURCHASE_RETURN_MESSAGES.qtyCap(billed, code, returned, left),
        key: 'preturn.errQtyCap',
        params: { billed, code, returned, left },
    }),
    notInStock: (onHand, code, warehouse, wanted) => ({
        message: PURCHASE_RETURN_MESSAGES.notInStock(onHand, code, warehouse, wanted),
        key: 'preturn.errNotInStock',
        params: { onHand, code, warehouse, wanted },
    }),
    cancelledBill: (code) => ({
        message: PURCHASE_RETURN_MESSAGES.cancelledBill(code),
        key: 'preturn.errCancelledBill',
        params: { code },
    }),
    cancelAfterApplied: (status) => ({
        message: PURCHASE_RETURN_MESSAGES.cancelAfterApplied(status),
        key: 'preturn.errCancelAfterApplied',
        params: { status },
    }),
};
/**
 * PUR-04 §5 field 5: "Read from the same five-value list the details line
 * uses" — SAL-00 §5d field 14, whose complete list is these five, "shared
 * with the purchase return". The list is not forked for this screen.
 */
export const PurchaseReturnReasonSchema = z.enum([
    'damaged_goods',
    'wrong_item_shipped',
    'customer_changed_mind',
    'overstock',
    'other',
]);
/**
 * BR-PUR-04-09: "The reason fallback must be 'Other', not 'Damaged goods'."
 * An unrecognised reason silently claiming the goods were damaged is a lie
 * the accounts carry; falling back to Other says only that nobody knows.
 */
export const PURCHASE_RETURN_REASON_FALLBACK = 'other';
export function normalizePurchaseReturnReason(value) {
    const parsed = PurchaseReturnReasonSchema.safeParse(value);
    return parsed.success ? parsed.data : PURCHASE_RETURN_REASON_FALLBACK;
}
// ── Shapes ──────────────────────────────────────────────────────────────────
/**
 * One returning line: WHICH billed line and HOW MANY go back. Everything else
 * — description, price, discount — is copied from the bill line server-side
 * (BR-PUR-04-06): the wire cannot even express a repriced return, which is
 * how "must not be editable" is enforced rather than asked for.
 */
export const PurchaseReturnLineInputSchema = z
    .object({
    /** The billed line's number on the bill (1-based, SAL-00 §5d). */
    billLineNo: z.number().int().positive(),
    qty: QtyStringSchema,
    /**
     * BR-MD-03-17 does not point one way. A serial-tracked part must go BACK
     * by name: it came in named at the goods receipt, and leaving unnamed
     * would keep it on the shelf in the ledger for ever — the next document
     * naming it would be refused, correctly, and nobody could say why.
     */
    serialNos: z.string().trim().min(1).max(60).array().max(1000).optional(),
    batchNo: z.string().trim().min(1).max(40).optional(),
    /**
     * Wave 5D — where THIS line's goods leave from. Omitted = where they came
     * in (the bill line's receiving warehouse, else the goods receipt's), so
     * a return that names nothing behaves exactly as before.
     */
    warehouseId: z.uuid().optional(),
})
    .strict();
/**
 * Wave 5D — one FREE line of a DIRECT return (a debit note with no bill
 * behind it): the item going back, where from, how many, and a price that
 * DEFAULTS to the supplier's last posted bill price for the item (else the
 * item's cost price) and stays editable — there is no billed line to copy
 * from, so BR-PUR-04-06's copy rule has nothing to copy. Nothing is capped
 * (BR-PUR-04-04 is a rule about the bill relationship); the editor WARNS when
 * more goes back than was ever bought from this supplier, through
 * `partners.supplierItemHistory`.
 */
export const PurchaseReturnFreeLineInputSchema = z
    .object({
    itemId: z.uuid(),
    warehouseId: z.uuid().optional(),
    /** Omitted = the item's name. */
    description: z.string().trim().min(1).max(200).optional(),
    qty: QtyStringSchema,
    /** Omitted = the supplier's last posted price for the item, else the item's cost price. */
    unitPrice: MoneyStringSchema.optional(),
    discountPct: PctStringSchema.optional(),
    serialNos: z.string().trim().min(1).max(60).array().max(1000).optional(),
    batchNo: z.string().trim().min(1).max(40).optional(),
})
    .strict();
/** A line is one shape or the other; a document is all of one shape. */
export const PurchaseReturnAnyLineInputSchema = z.union([
    PurchaseReturnLineInputSchema,
    PurchaseReturnFreeLineInputSchema,
]);
export const PurchaseReturnLineSchema = z
    .object({
    lineNo: z.number().int().positive(),
    /** The billed line this returns — null on a DIRECT return's free line (wave 5D). */
    billLineNo: z.number().int().positive().nullable(),
    /** Which catalogue item is going back — null for a free-text/service line. */
    itemId: z.uuid().nullable(),
    /** ITM-#### — the code every §14 sentence names. */
    itemCode: z.string().nullable(),
    description: z.string(),
    /** What is going back this time. */
    qty: z.string(),
    /** What the bill charged on this line, for the editor's cap display; null on a free line. */
    billedQty: z.string().nullable(),
    /** Gone back across OTHER posted returns (BR-PUR-04-04's "already"); null on a free line. */
    alreadyReturnedQty: z.string().nullable(),
    /** What is on the shelf where the goods came in (BR-PUR-04-05's cap). */
    onHandQty: z.string(),
    /**
     * `N-4` — the unit all three caps are in: billed, already returned, and
     * on hand.
     *
     * The item's STOCK unit, and here that is the LINE's unit too:
     * `purchase_invoice_lines` carries no `unit_id` (the column exists on
     * sales `invoice_lines` alone), so a bill line is already written in the
     * stock unit — which is what lets `onHandQty`, a shelf balance, be
     * compared with it at all. `BR-MD-03-04d` has nothing to disagree with.
     *
     * Both null for a free-text or service line: no item, no unit of measure.
     */
    unitSymbol: z.string().nullable(),
    unitPrecision: UnitPrecisionSchema.nullable(),
    /**
     * Where the goods LEAVE from: the line's own warehouse when one was
     * picked (wave 5D), else where they came in — so the editor can name it
     * in the refusal and draw the select.
     */
    warehouseId: z.uuid().nullable(),
    warehouseName: z.string(),
    /**
     * BR-PUR-04-07: the cost the goods ENTERED at, not today's average. On a
     * DIRECT return's line it is the location's current average cost — the
     * figure the goods WILL leave at, there being no inbound layer to reverse.
     */
    entryUnitCost: z.string(),
    unitPrice: z.string(),
    discountPct: z.string(),
    lineTotal: z.string(),
    /** What is going back, for a tracked item (BR-MD-03-17). */
    serialNos: z.string().array().nullable(),
    batchNo: z.string().nullable(),
})
    .strict();
/**
 * PUR-04 §7 plus BR-PUR-04-01 and BR-PUR-04-02. The screen's three are
 * Draft / Approved / Credited; approving IS posting here (§6's green button
 * reads "Post return" and §9 gates approval on the same permission), so
 * `posted` is §7's Approved. Cancelled and Refunded exist because the two
 * business rules require them to.
 */
export const PurchaseReturnStatusSchema = z.enum([
    'draft',
    'posted',
    'credited',
    'refunded',
    'cancelled',
]);
export const PurchaseReturnSchema = z
    .object({
    id: z.uuid(),
    /** DBN-#### — its own prefix, never the delivery note's (BR-PUR-04-12). */
    code: z.string(),
    returnNo: z.number().int().positive(),
    status: PurchaseReturnStatusSchema,
    returnDate: IsoDateSchema,
    partnerId: z.uuid(),
    partnerCode: z.string(),
    /** The strip's right-hand name: "Acme Raw Materials". */
    partnerName: z.string(),
    /**
     * The strip pill: "Against bill · BILL-####" — clicks through. NULL on a
     * DIRECT return (wave 5D): the note names the supplier and free lines
     * with no bill behind it; `billCode` is then the empty string.
     */
    billId: z.uuid().nullable(),
    billCode: z.string(),
    /** Wave 5D — true when `billId` is null: free lines, no caps, average-cost stock out. */
    isStandalone: z.boolean(),
    /** Wave 5D header fields — the supplier's own reference, the physical book number, the buyer. */
    manualRef: z.string().nullable(),
    bookNo: z.string().nullable(),
    buyerUserId: z.uuid().nullable(),
    buyerName: z.string().nullable(),
    /**
     * Wave 5D — the per-document override of the balancing leg's account
     * (null = `purchasing.returns_account`, else 5050 Purchase Price
     * Variance). It reaches ONLY the price-difference leg — never 2111 and
     * never inventory. See the service's `returnsAccountFor`.
     */
    returnsAccountId: z.uuid().nullable(),
    reason: PurchaseReturnReasonSchema,
    reasonNote: z.string().nullable(),
    /** BR-PUR-04-08 — shown on the screen at last, and driving §12. */
    restocked: z.boolean(),
    /** §15's value-only return: money back, no stock movement. */
    valueOnly: z.boolean(),
    currency: z.string(),
    /** EC-19-10: the rate this document froze when it was confirmed, and its "as of" date. */
    ...frozenRateFields,
    /** Wave 4D — the typed-rate flag and the market hint (fx.ts). */
    ...rateOverrideFields,
    lines: PurchaseReturnLineSchema.array(),
    subtotal: z.string(),
    discountPct: z.string(),
    discountAmount: z.string(),
    taxPct: z.string(),
    taxAmount: z.string(),
    /** The strip's blue number: what the supplier owes back. */
    grandTotal: z.string(),
    /** §5 field 6: how many units are going back, summed over the lines. */
    qtyReturned: z.string(),
    notes: z.string().nullable(),
    journalEntryId: z.uuid().nullable(),
    postedAt: z.string().nullable(),
    /** GEN-72's stamp trio — set once by cancel, null otherwise. */
    cancelledAt: z.string().nullable(),
    cancelReason: z.string().nullable(),
    cancellationJournalEntryId: z.uuid().nullable(),
    /** BR-PUR-04-02's stamp pair — set once by the refund action. */
    refundedAt: z.string().nullable(),
    refundJournalEntryId: z.uuid().nullable(),
    createdAt: z.string(),
})
    .strict();
/**
 * Wave 3B — the debit-note list's tiles over the WHOLE register; the shape
 * every `/stats` route shares is explained in `document-stats.ts`. Defined
 * here rather than there because it names `PurchaseReturnReasonSchema`.
 */
export const PurchaseReturnStatsSchema = z
    .object({
    /** Σ grand totals of returns posted or beyond (credited/refunded) dated this calendar month, in base. */
    returnsThisMonthValue: MoneyStringSchema,
    count: z.number().int().min(0),
    /** Posted and not yet credited or refunded by the supplier. */
    awaitingSupplierCredit: z.number().int().min(0),
    /** The most frequent reason over the last 90 days (any status but cancelled); null when there were none. */
    topReason: PurchaseReturnReasonSchema.nullable(),
    ...StatsCurrencyField,
})
    .strict();
export const RefundPurchaseReturnRequestSchema = z
    .object({
    /** PUR-04 §12 row 4: where the supplier's money lands (DR this account). */
    depositToAccountId: z.uuid(),
})
    .strict();
export const CancelPurchaseReturnRequestSchema = z
    .object({
    /** GEN-72: cancelling must ask for a reason and store it. */
    reason: z.string().trim().min(1, PURCHASE_RETURN_MESSAGES.cancelReasonRequired).max(500),
})
    .strict();
/** Wave 5D's header fields, shared by create and update. */
const headerFields = {
    /** The supplier's own reference for this return (their RMA / credit request number). */
    manualRef: z.string().trim().max(60).optional(),
    /** The physical book / register number the return was written in. */
    bookNo: z.string().trim().max(40).optional(),
    /** The buyer responsible for the return — a user of this company. */
    buyerUserId: z.uuid().optional(),
    /** Per-document override of the price-difference leg's account; omitted = the company setting, else 5050. */
    returnsAccountId: z.uuid().optional(),
};
export const CreatePurchaseReturnRequestSchema = z
    .object({
    /** Wave 4D — a typed exchange rate on the draft (fx.ts). */
    ...rateOverrideInputFields,
    /** Per-document currency (fx.ts documentCurrencyInputFields). */
    ...documentCurrencyInputFields,
    /**
     * BR-PUR-04-03: the POSTED bill this return is against. Omitted = a
     * DIRECT return (wave 5D): the note names `partnerId` and free lines.
     */
    billId: z.uuid().optional(),
    /** Wave 5D: the supplier a DIRECT return goes back to. Ignored (the bill's) when `billId` is given. */
    partnerId: z.uuid().optional(),
    returnDate: IsoDateSchema,
    reason: PurchaseReturnReasonSchema,
    reasonNote: z.string().trim().max(500).optional(),
    /** BR-PUR-04-08: whether the goods are sellable stock going back. */
    restocked: z.boolean().optional(),
    /** §15: claim the money with no stock movement. */
    valueOnly: z.boolean().optional(),
    /**
     * Bill-linked: omitted = a FULL return, every billed line at its full
     * remaining qty. Direct: required, free lines.
     */
    lines: PurchaseReturnAnyLineInputSchema.array().min(1).optional(),
    /** Direct only: F-2's document discount; omitted = none. Bill-linked returns copy the bill's. */
    discount: TwoWayAmountSchema.optional(),
    /** Direct only: F-4's VAT box; omitted = the company default rate. Bill-linked returns copy the bill's. */
    tax: TwoWayAmountSchema.optional(),
    ...headerFields,
    notes: z.string().max(2000).optional(),
})
    .strict()
    .refine((body) => body.billId !== undefined || body.partnerId !== undefined, {
    message: PURCHASE_RETURN_MESSAGES.pickSupplier,
    path: ['partnerId'],
});
export const UpdatePurchaseReturnRequestSchema = z
    .object({
    /** Wave 4D — a typed exchange rate on the draft (fx.ts). */
    ...rateOverridePatchFields,
    /** Per-document currency, draft only (fx.ts documentCurrencyPatchFields). */
    ...documentCurrencyPatchFields,
    returnDate: IsoDateSchema.optional(),
    reason: PurchaseReturnReasonSchema.optional(),
    reasonNote: z.string().trim().max(500).nullable().optional(),
    restocked: z.boolean().optional(),
    valueOnly: z.boolean().optional(),
    /** Present = replace the returning set (draft only), in the document's own shape of line. */
    lines: PurchaseReturnAnyLineInputSchema.array().min(1).optional(),
    /** Direct only (wave 5D). */
    discount: TwoWayAmountSchema.optional(),
    tax: TwoWayAmountSchema.optional(),
    manualRef: z.string().trim().max(60).nullable().optional(),
    bookNo: z.string().trim().max(40).nullable().optional(),
    buyerUserId: z.uuid().nullable().optional(),
    returnsAccountId: z.uuid().nullable().optional(),
    /** The ONE field editable after posting (BR-SAL-00-22). */
    notes: z.string().max(2000).nullable().optional(),
})
    .strict();
const purchaseReturnParams = z.object({ returnId: z.uuid() }).strict();
export const purchaseReturnsContract = c.router({
    list: {
        method: 'GET',
        path: '/api/purchases/returns',
        query: pagedQuery({
            partnerId: z.uuid().optional(),
            billId: z.uuid().optional(),
            status: PurchaseReturnStatusSchema.optional(),
            /** GEN-50 / the header rail's Find: the printed number or the supplier's name. */
            q: z.string().trim().max(120).optional(),
            /** Wave 3B — bounds on the return date, both inclusive. */
            ...DocumentDateRangeQuery,
        }),
        responses: {
            200: pagedResponse('returns', PurchaseReturnSchema),
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'Debit notes (purchasing.view), PAGED (NFR-42) — all three filters in SQL. Drafts appear here and nowhere else — R9 keeps them off statements and payables reports.',
    },
    get: {
        method: 'GET',
        path: '/api/purchases/returns/:returnId',
        pathParams: purchaseReturnParams,
        responses: {
            200: PurchaseReturnSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One debit note with its lines and caps (purchasing.view).',
    },
    /**
     * S4 — the document header's ‹‹ ›› rail (wave 2 of specs/erp-gap-analysis.md):
     * the debit notes on either side of this one in number order (null at an
     * end). See `document-navigation.ts`.
     */
    neighbours: {
        method: 'GET',
        path: '/api/purchases/returns/:returnId/neighbours',
        pathParams: purchaseReturnParams,
        responses: {
            200: DocumentNeighboursSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'The previous and next debit note by number (purchasing.view).',
    },
    /** S4 — the "Go to no." box: one debit note by its printed number; 404 on a miss. */
    lookup: {
        method: 'GET',
        path: '/api/purchases/returns/lookup',
        query: DocumentLookupQuerySchema,
        responses: {
            200: DocumentLookupResponseSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
        },
        summary: 'One debit note by its printed number (purchasing.view).',
    },
    create: {
        method: 'POST',
        path: '/api/purchases/returns',
        body: CreatePurchaseReturnRequestSchema,
        responses: {
            201: PurchaseReturnSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: "New draft return (purchasing.create). Against a POSTED bill: lines loaded from the bill with billed, already-returned and on-hand quantities, prices copied (BR-PUR-04-06), caps applied (BR-PUR-04-04). DIRECT (no billId, wave 5D): the supplier and free lines, prices defaulting to the supplier's last posted price, nothing capped.",
    },
    update: {
        method: 'PATCH',
        path: '/api/purchases/returns/:returnId',
        pathParams: purchaseReturnParams,
        body: UpdatePurchaseReturnRequestSchema,
        responses: {
            200: PurchaseReturnSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Edit a DRAFT (purchasing.edit); a posted debit note answers 409 except a notes-only change (R8, BR-PUR-04-13).',
    },
    remove: {
        method: 'DELETE',
        path: '/api/purchases/returns/:returnId',
        pathParams: purchaseReturnParams,
        body: c.noBody(),
        responses: {
            204: c.noBody(),
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Delete a DRAFT (purchasing.delete); posted answers 409 (BR-PUR-04-13).',
    },
    post: {
        method: 'POST',
        path: '/api/purchases/returns/:returnId/post',
        pathParams: purchaseReturnParams,
        body: z.object({}).strict().optional(),
        responses: {
            200: PurchaseReturnSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: 'Post return (purchasing.approve) — Draft → Posted: writes the supplier_return stock movements at the cost the goods ENTERED at (BR-PUR-04-07) and the PUR-04 §12 entry through the ledger in ONE transaction (DR 2111 Trade Payables / CR 1131 Inventory / CR 2120 VAT, the balancing difference to 5050, plus DR 5030 when the goods were scrapped rather than returned to the shelf), lowers what we owe, locks the document.',
    },
    cancel: {
        method: 'POST',
        path: '/api/purchases/returns/:returnId/cancel',
        pathParams: purchaseReturnParams,
        body: CancelPurchaseReturnRequestSchema,
        responses: {
            200: PurchaseReturnSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: "Cancel a POSTED debit note (purchasing.approve — BR-PUR-04-01/-13: 'a debit note raised in error is reversed by an opposite entry, which is what the Cancelled status is for'): reverses the §12 entry on the note's own date (GEN-72) and brings the goods back at the cost they left at. Credited/refunded notes refuse — un-apply the money first (GEN-74). Refused into a closed period (GEN-73). Reason required and audited.",
    },
    refund: {
        method: 'POST',
        path: '/api/purchases/returns/:returnId/refund',
        pathParams: purchaseReturnParams,
        body: RefundPurchaseReturnRequestSchema,
        responses: {
            200: PurchaseReturnSchema,
            400: MessageSchema,
            401: MessageSchema,
            403: MessageSchema,
            404: MessageSchema,
            409: MessageSchema,
        },
        summary: "Record the supplier's cash refund (accounting.approve — money in is the treasurer's act): DR the chosen cash/bank account / CR 2111 Trade Payables at the note's grand total (PUR-04 §12 row 4), the GEN-113 exchange difference on the refund date (§15 'A cash refund at another rate does create one'), status → refunded (BR-PUR-04-02). Posted, unapplied notes only; once ever.",
    },
    /** Wave 3B — the list's tiles over the WHOLE register (see `document-stats.ts`). */
    stats: {
        method: 'GET',
        path: '/api/purchases/returns/stats',
        responses: {
            200: PurchaseReturnStatsSchema,
            401: MessageSchema,
            403: MessageSchema,
        },
        summary: 'PUR-04 list tiles (purchasing.view): returns this month (value, count), awaiting supplier credit, the top reason over 90 days — base currency, in SQL.',
    },
}, { strictStatusCodes: true });
//# sourceMappingURL=purchase-returns.js.map